import React from "react";
import {
  MdArrowDownward,
  MdArrowUpward,
  MdCall,
  MdCheck,
  MdClose,
  MdNavigation,
  MdPlace,
} from "react-icons/md";

import FulfilmentCell from "../Cells/FulfilmentCell";
import OrderNotes, { orderNotes } from "./OrderNotes";
import SheetChip, { LINE_STATES } from "./SheetChip";
import LineNotes from "./OrderLineNotes";
import {
  deliveryAddress,
  navigateUrl,
  orderPieces,
  orderWeight,
  productName,
} from "./routeSheet";
import { telHref } from "./routeMapStops";
import { LOCKED_HINT } from "./runs";
import { metersText } from "./driverLocation";

const customerName = (shipping) =>
  [shipping?.first_name, shipping?.last_name]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");

// The table's small controls, as they were.
const ICON_BTN =
  "grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[6px] text-ui-text-subtle hover:bg-ui-surface-sunken hover:text-ui-text disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent";
// The card's: 40px, so a thumb hits the right one.
const CARD_BTN =
  "grid h-[40px] w-[40px] shrink-0 place-items-center rounded-[7px] border-[1px] border-ui-border-strong bg-ui-surface text-ui-text transition-colors hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:border-ui-border-strong disabled:hover:text-ui-text";

// How each piece looks in the table row (unchanged) and on the phone card.
// Only the styling differs: what a piece shows and does is the same.
const LOOKS = {
  row: {
    open: "block border-0 bg-transparent p-[0px] font-bold text-brand hover:underline print:text-ui-text print:no-underline",
    id: "block font-bold",
    tracking: "block font-mono text-[10.5px] text-ui-text-subtle",
    phone: "block text-ui-text-muted hover:text-brand",
    phoneIcon: null,
    navigate:
      "mr-[6px] flex items-center gap-[4px] whitespace-nowrap font-semibold text-brand hover:underline",
    navigateIcon: "h-[13px] w-[13px]",
    button: ICON_BTN,
    buttonIcon: "h-[14px] w-[14px]",
    tick: "h-[15px] w-[15px] align-middle accent-brand",
  },
  card: {
    open: "inline-flex min-h-[40px] items-center border-0 bg-transparent p-[0px] font-bold text-brand hover:underline",
    id: "font-bold text-ui-text",
    tracking: "font-mono text-[11.5px] text-ui-text-subtle",
    phone:
      "inline-flex min-h-[40px] items-center gap-[5px] font-semibold text-brand hover:underline",
    phoneIcon: "h-[15px] w-[15px] shrink-0",
    // Grows from its own width, so on a very narrow phone the arrows wrap
    // under it instead of squeezing it.
    navigate:
      "flex min-h-[40px] flex-[1_1_auto] items-center justify-center gap-[6px] whitespace-nowrap rounded-[7px] border-[1px] border-ui-border bg-ui-surface px-[12px] text-[13px] font-semibold text-brand transition-colors hover:border-brand hover:bg-brand-light",
    navigateIcon: "h-[16px] w-[16px] shrink-0",
    button: CARD_BTN,
    buttonIcon: "h-[18px] w-[18px]",
    tick: "h-[20px] w-[20px] accent-brand",
  },
};

// A stop's items: each line's state (fulfilled lines struck through, so
// nobody packs them twice), lines not linked to a product, the stock at the
// order's location, and a Missing report from the pick list (`missing`: line
// ids).
const StopItems = ({ lines, missing }) => (
  <div className="grid gap-[4px]">
    {!lines.length && <span className="text-ui-text-subtle">No items</span>}
    {lines.map((line) => {
      const [label, tone] = LINE_STATES[line.state];
      const unlinked = line.flag === "unlinked";
      const done = line.state === "fulfilled";
      return (
        <div
          key={line.item?.id}
          className="flex flex-wrap items-center gap-x-[6px] gap-y-[2px]"
        >
          <SheetChip tone={tone}>
            {done && <MdCheck className="h-[11px] w-[11px]" />}
            {label}
          </SheetChip>
          <span className={done ? "text-ui-text-muted line-through" : ""}>
            {line.quantity} × {productName(line.item) || "-"}
          </span>
          {line.state === "attention" && line.fulfilled > 0 && (
            <span className="text-ui-text-muted">
              {line.fulfilled} of {line.quantity} fulfilled
            </span>
          )}
          {unlinked && <SheetChip tone="danger">Not in Products</SheetChip>}
          {line.stock != null &&
            line.state !== "fulfilled" &&
            line.state !== "cancelled" && (
              <span
                title="Stock count at this order's location. It already has every open order taken off, this one included."
                className="text-[11px] text-ui-text-muted"
              >
                Current stock: {line.stock}
              </span>
            )}
          {line.open && missing.has(String(line.item?.id)) && (
            <SheetChip tone="danger">Missing</SheetChip>
          )}
          {!done && <LineNotes item={line.item} />}
        </div>
      );
    })}
  </div>
);

/**
 * Where a recorded stop was saved (the phone's position then): how far it
 * was from the address, in red past FAR_METERS (worth a look: the wrong
 * door, or the GPS was off). `spot` as stopSpot gives it, or null.
 */
export const SpotLine = ({ spot, className = "" }) => {
  if (!spot) return null;
  return (
    <span
      title={
        spot.accuracy != null
          ? `The phone's position when it was recorded, give or take ${metersText(spot.accuracy)}`
          : "The phone's position when it was recorded"
      }
      className={`flex items-center gap-[4px] text-[11.5px] font-semibold ${
        spot.far ? "text-state-danger-text" : "text-state-success-text"
      } ${className}`}
    >
      <MdPlace aria-hidden="true" className="h-[13px] w-[13px] shrink-0" />
      {spot.distance == null
        ? "Recorded with its location"
        : `Recorded ${metersText(spot.distance)} from the address`}
    </span>
  );
};

/**
 * One stop's pieces on the route sheet, built once for its table row and
 * its phone card alike, so both show the same and every control keeps one
 * handler, label and set of rules: the stop number, the order (#id, which
 * opens it, and its reference), the status dropdown, tracking code, notes,
 * customer and phone, address, items, pieces and weight, and the Navigate,
 * ↑, ↓ and × controls and the box that ticks it to move. `look` ("row" or
 * "card") changes sizes only. `sheet` is what the sheet shares with every
 * stop: { isRun, canEditRun, locked, busy, count, ticked (order ids),
 * canMove(orderId), removable(orderId), onTick(orderId), onMove(index,
 * delta), onRemove(orderId), onOpen(order) or null, onStatusChange(event,
 * order) or null, statuses, canFulfil(order), missing (line ids reported
 * missing), formatLb(lb) }.
 */
export const stopParts = ({
  order,
  index,
  lines = [],
  sheet,
  look = "row",
}) => {
  const style = LOOKS[look];
  const shipping = order?.order_shipping_address_details;
  const address = deliveryAddress(shipping);
  const lb = orderWeight(order);
  const tracking = order?.self_delivery_tracking?.tracking_id;
  const movable = sheet.canMove(order.id);
  // ↑/↓ are there for ticked orders and on a run still open; on a run they
  // wait while a change saves and while it's locked.
  const reorders = !sheet.isRun || sheet.canEditRun;
  const held = sheet.isRun && (sheet.busy || sheet.locked);
  const moveButton = (delta) => (
    <button
      type="button"
      aria-label={`Move #${order.id} ${delta < 0 ? "up" : "down"}`}
      title={sheet.locked ? LOCKED_HINT : undefined}
      disabled={(delta < 0 ? index === 0 : index === sheet.count - 1) || held}
      onClick={() => sheet.onMove(index, delta)}
      className={style.button}
    >
      {delta < 0 ? (
        <MdArrowUpward className={style.buttonIcon} />
      ) : (
        <MdArrowDownward className={style.buttonIcon} />
      )}
    </button>
  );

  return {
    number: index + 1,
    orderId: sheet.onOpen ? (
      <button
        type="button"
        aria-label={`Open order #${order.id}`}
        title="Open the order"
        onClick={() => sheet.onOpen(order)}
        className={style.open}
      >
        #{order.id}
      </button>
    ) : (
      <span className={style.id}>#{order.id}</span>
    ),
    reference: order?.order_id,
    status: sheet.onStatusChange ? (
      <FulfilmentCell
        row={order}
        orderStatus={sheet.statuses}
        canEdit={sheet.canFulfil(order)}
        showLocation={false}
        showCharges={false}
        onStatusChange={sheet.onStatusChange}
      />
    ) : null,
    tracking: tracking ? (
      <span title="Tracking code" className={style.tracking}>
        {tracking}
      </span>
    ) : null,
    notes: orderNotes(order).length ? <OrderNotes order={order} /> : null,
    customer: customerName(shipping) || "-",
    phone: shipping?.phone ? (
      <a href={telHref(shipping.phone)} className={style.phone}>
        {style.phoneIcon && (
          <MdCall aria-hidden="true" className={style.phoneIcon} />
        )}
        {shipping.phone}
      </a>
    ) : null,
    address: address || (
      <span className="text-state-danger-text">No delivery address</span>
    ),
    items: <StopItems lines={lines} missing={sheet.missing} />,
    where: sheet.spots?.[order.id] ? (
      <SpotLine spot={sheet.spots[order.id]} className="mt-[4px]" />
    ) : null,
    pieces: orderPieces(order),
    weight: lb != null ? sheet.formatLb(lb) : null,
    // Ticks a stop still waiting for its outcome to move it to another run.
    tick: sheet.canEditRun ? (
      <input
        type="checkbox"
        aria-label={`Tick #${order.id} to move it`}
        title={
          sheet.locked
            ? LOCKED_HINT
            : movable
              ? undefined
              : "A stop with its outcome stays on this run"
        }
        checked={sheet.ticked.includes(order.id)}
        disabled={sheet.busy || !movable}
        onChange={() => sheet.onTick(order.id)}
        className={style.tick}
      />
    ) : null,
    navigate: address ? (
      <a
        href={navigateUrl(address)}
        target="_blank"
        rel="noopener noreferrer"
        className={style.navigate}
      >
        <MdNavigation className={style.navigateIcon} />
        Navigate
      </a>
    ) : null,
    up: reorders ? moveButton(-1) : null,
    down: reorders ? moveButton(1) : null,
    remove: sheet.removable(order.id) ? (
      <button
        type="button"
        aria-label={`Take #${order.id} off the run`}
        title={sheet.locked ? LOCKED_HINT : "Take off the run"}
        disabled={sheet.busy || sheet.locked}
        onClick={() => sheet.onRemove(order.id)}
        className={style.button}
      >
        <MdClose className={style.buttonIcon} />
      </button>
    ) : null,
  };
};

/**
 * A stop as a row of the stops table (768px and up), laid out as before:
 * tick box (on a run), #, order, customer, address, items, pieces, weight,
 * then Navigate, ↑, ↓ and ×.
 */
export const StopRow = ({ parts }) => {
  const reorder = parts.up || parts.down || parts.remove;
  return (
    <tr className="border-b-[1px] border-ui-border-subtle align-top last:border-b-0">
      {parts.tick && (
        <td className="px-[10px] py-[9px] print:hidden">{parts.tick}</td>
      )}
      <td className="px-[10px] py-[9px] font-semibold">{parts.number}</td>
      <td className="px-[10px] py-[9px]">
        {parts.orderId}
        <span className="block text-ui-text-muted">{parts.reference}</span>
        {parts.status && (
          <div className="mt-[4px] flex max-w-[160px] print:hidden">
            {parts.status}
          </div>
        )}
        {parts.tracking}
        <span className="mt-[4px] block">{parts.notes}</span>
      </td>
      <td className="px-[10px] py-[9px]">
        <span className="block">{parts.customer}</span>
        {parts.phone}
      </td>
      <td className="px-[10px] py-[9px]">
        {parts.address}
        {parts.where}
      </td>
      <td className="px-[10px] py-[9px]">{parts.items}</td>
      <td className="px-[10px] py-[9px] text-right">{parts.pieces}</td>
      <td className="whitespace-nowrap px-[10px] py-[9px] text-right">
        {parts.weight ?? "-"}
      </td>
      <td className="px-[10px] py-[7px] print:hidden">
        {/* On a tablet in portrait the arrows may wrap under Navigate, so
            the table fits without scrolling sideways; from lg up they
            stay on one line. */}
        <div className="flex [flex-wrap:wrap] items-center justify-end gap-[2px] lg:flex-nowrap">
          {parts.navigate}
          {reorder && (
            <div className="flex items-center gap-[2px]">
              {parts.up}
              {parts.down}
              {parts.remove}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

/**
 * A stop as a card (phones, below 768px): the stop number big, the order's
 * reference with its #id, pieces and weight; the customer with tap-to-call;
 * the address; the items; the tracking code, notes and status; and a row of
 * 40px controls — the tick box (on a run), Navigate, ↑, ↓ and ×.
 */
export const StopCard = ({ parts }) => {
  const load = [`${parts.pieces} pcs`, parts.weight].filter(Boolean);
  const extras = parts.tracking || parts.notes || parts.status;
  const reorder = parts.up || parts.down || parts.remove;
  const controls = parts.tick || parts.navigate || reorder;
  return (
    <li>
      <article
        aria-label={`Stop ${parts.number}`}
        className="grid gap-[8px] rounded-[9px] border-[1px] border-ui-border bg-ui-surface p-[12px] text-[12.5px]"
      >
        <div className="flex items-start gap-[10px]">
          <span className="grid h-[36px] min-w-[36px] shrink-0 place-items-center rounded-full bg-brand px-[6px] text-[17px] font-extrabold leading-none text-white">
            {parts.number}
          </span>
          <div className="grid min-w-[0px] flex-1">
            {parts.reference && (
              <span className="text-[14.5px] font-extrabold leading-[1.3] text-ui-text">
                {parts.reference}
              </span>
            )}
            <div className="flex flex-wrap items-center gap-x-[8px] text-[12px] text-ui-text-muted">
              {parts.orderId}
              {load.map((part) => (
                <span key={part}>· {part}</span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-[12px] text-[13px]">
          <span className="font-semibold">{parts.customer}</span>
          {parts.phone}
        </div>
        <div className="text-[13px] leading-[1.4]">
          {parts.address}
          {parts.where}
        </div>
        {parts.items}
        {extras && (
          <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px]">
            {parts.tracking}
            {parts.notes}
            {parts.status && (
              <div className="flex max-w-[200px]">{parts.status}</div>
            )}
          </div>
        )}
        {controls && (
          <div className="flex flex-wrap items-center gap-[8px] border-t-[1px] border-ui-border-subtle pt-[10px] print:hidden">
            {/* The box round the tick makes a 40px target that ticks it
                (min-w-[0px]: a global label rule would make it 105px). */}
            {parts.tick && (
              <label className="grid h-[40px] w-[40px] min-w-[0px] shrink-0 place-items-center rounded-[7px] border-[1px] border-ui-border-strong">
                {parts.tick}
              </label>
            )}
            {parts.navigate}
            {reorder && (
              <div className="ml-auto flex shrink-0 items-center gap-[8px]">
                {parts.up}
                {parts.down}
                {parts.remove}
              </div>
            )}
          </div>
        )}
      </article>
    </li>
  );
};
